import { useFlags } from "launchdarkly-react-client-sdk";

import { useAttributesContext } from "shared/contexts/AttributesContext";
import { ContextProviderName } from "shared/contexts/types";
import {
  GENERIC_FILTER,
  REPAIRS_GENERIC_FILTER,
  WORK_ORDERS_GENERIC_FILTER,
} from "shared/filterDefinitions";

import { VEHICLE_ACCESSOR } from "./constants";
import usePartSchema from "./partSchema";
import { useRepairsSchemaV2 } from "./repairsSchema";
import useServiceCentersSchema, {
  useServiceCentersSchemaV2,
} from "./serviceCentersSchema";
import { SchemaEntryOverride, UseSchema } from "./types";
import {
  createMinimalSchema,
  getAttributeMap,
  getDisplayLabelFunction,
  getFilterFunction,
  getSchemaEntryFunction,
  getSchemaForAccessorFunction,
  getSchemaFromAttributes,
} from "./utils/schema-utils";
import useWorkOrderPartsSchema from "./workOrderPartsSchema";

interface WorkOrdersSchemaProps {
  accessorsToHide?: string[];
  includeRepairsRelation?: boolean;
}

const useWorkOrdersSchema = ({
  accessorsToHide = [],
  includeRepairsRelation = true,
}: WorkOrdersSchemaProps = {}): UseSchema => {
  const { attributes: workOrderAttributes } = useAttributesContext(
    ContextProviderName.WorkOrder
  );
  const workOrderAttributeMap = getAttributeMap(workOrderAttributes);
  const getWorkOrderDisplayLabel = getDisplayLabelFunction(
    workOrderAttributeMap
  );
  const getWorkOrderFilter = getFilterFunction(
    WORK_ORDERS_GENERIC_FILTER,
    workOrderAttributeMap
  );
  const getWorkOrderSchemaEntry = getSchemaEntryFunction(
    getWorkOrderFilter,
    workOrderAttributeMap
  );

  const partsSchema = usePartSchema();
  const workOrderPartsSchema = useWorkOrderPartsSchema();
  const serviceCentersSchema = useServiceCentersSchema([
    "createdAt",
    "updatedAt",
  ]);

  const { attributes: repairAttributes } = useAttributesContext(
    ContextProviderName.Repair
  );
  const { warrantyClaimsCost: warrantyClaimsCostFF } = useFlags();

  const workOrderOverrides: SchemaEntryOverride = {};

  const workOrderRelationships: Record<string, UseSchema> = {
    "/api/v1/workOrderParts": workOrderPartsSchema,
    "/api/v1/serviceCenters": serviceCentersSchema,
  };

  if (includeRepairsRelation) {
    // Create a minimal repairs schema without the work orders relationship
    const repairOverrides: SchemaEntryOverride = {
      [VEHICLE_ACCESSOR]: {
        schemaOverride: {
          filter: undefined,
        },
      },
      costLabor: { ignore: !warrantyClaimsCostFF },
      costParts: { ignore: !warrantyClaimsCostFF },
      costOther: { ignore: !warrantyClaimsCostFF },
    };

    const repairsSchemaForWorkOrders = createMinimalSchema(
      repairAttributes,
      REPAIRS_GENERIC_FILTER,
      repairOverrides,
      { "/api/v1/parts": partsSchema }
    );

    workOrderRelationships["/api/v1/repairs"] = repairsSchemaForWorkOrders;
  }

  const workOrderSchema = getSchemaFromAttributes(
    workOrderAttributes,
    getWorkOrderSchemaEntry,
    workOrderOverrides,
    workOrderRelationships,
    accessorsToHide
  );

  const workOrdersUseSchema: UseSchema = {
    schema: workOrderSchema,
    getDisplayLabel: getWorkOrderDisplayLabel,
    attributeMap: workOrderAttributeMap,
    attributes: workOrderAttributes,
    attributeAccessors: workOrderSchema.map(
      ({ accessor, filter }) => filter?.fieldName || accessor
    ),
    getSchemaEntry: getWorkOrderSchemaEntry,
    getSchemaForAccessor: getSchemaForAccessorFunction(workOrderSchema),
  };

  return workOrdersUseSchema;
};

const V2_VALUES_ENDPOINT = "v2/workOrders";

const useWorkOrdersSchemaV2 = ({
  accessorsToHide = [],
}: WorkOrdersSchemaProps = {}): UseSchema => {
  const { attributes } = useAttributesContext(ContextProviderName.WorkOrderV2);
  const attributeMap = getAttributeMap(attributes);
  const getDisplayLabel = getDisplayLabelFunction(attributeMap);
  const getFilter = getFilterFunction(
    GENERIC_FILTER,
    attributeMap,
    V2_VALUES_ENDPOINT
  );
  const getSchemaEntry = getSchemaEntryFunction(getFilter, attributeMap);

  const serviceCentersSchema = useServiceCentersSchemaV2([
    "createdAt",
    "updatedAt",
  ]);

  const repairsSchema = useRepairsSchemaV2();

  const relationships: Record<string, UseSchema> = {
    "/api/v2/serviceCenters": serviceCentersSchema,
    "/api/v2/workOrders/repairs": repairsSchema,
  };

  const schema = getSchemaFromAttributes(
    attributes,
    getSchemaEntry,
    undefined,
    relationships,
    accessorsToHide
  );

  return {
    schema,
    getDisplayLabel,
    attributeMap,
    attributes,
    attributeAccessors: schema.map(
      ({ accessor, filter }) => filter?.fieldName || accessor
    ),
    getSchemaEntry,
    getSchemaForAccessor: getSchemaForAccessorFunction(schema),
  };
};

export { useWorkOrdersSchemaV2 };

export default useWorkOrdersSchema;
